import PropTypes from 'prop-types';

import {
  Box, Heading, SimpleGrid, Skeleton,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component} from '@amblique/king-living/app/components/contentful/component';
import {BlogSummary} from '@amblique/king-living/app/components/contentful/blog-summary';
import {Link as ContentfulLink} from '@amblique/king-living/app/contentful/models/link';
import {useResolvedInlinedBlogGridCtaBlogSysId} from '@amblique/king-living/app/hooks/use-resolved-inline-blog-grid-cta-sys-id';

/**
 * BlogGrid Style 1
 * Featured layout with heading, hero blog summary, grid of 3 blog summaries, and CTA
 *
 * @param {Object} props - Component props
 * @param {Object} props.component - BlogGrid component data
 * @param {string} [props.component.heading] - Section heading (storefront)
 * @param {string} [props.component.title] - Entry title (CMS only, not rendered)
 * @param {Object[]} props.component.blogSummariesCollection - Collection of blog summaries
 * @param {Object} props.component.cta - CTA link component
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @param {Object} props.styles - Component styles
 * @param {boolean} [props.forceInlineFeaturedCta] - CF-055: pin CTA in featured content column
 * @returns {React.ReactElement}
 */
export const BlogGridStyle1 = ({
  component,
  isComplete = true,
  isFetching = false,
  styles,
  forceInlineFeaturedCta = false,
  ...props
}) => {
  const {blogSummaryCollection, cta} = component;
  const headingText = typeof component?.heading === 'string' ? component.heading.trim() : '';
  const blogSummaries = (blogSummaryCollection?.items || []).filter(Boolean);

  const inlinedBlogGridCtaBlogSysId = useResolvedInlinedBlogGridCtaBlogSysId(cta, blogSummaries);

  // First item is the featured/hero blog summary
  const featuredBlog = blogSummaries[0];
  // Remaining items are displayed in a 3-column grid
  const gridBlogs = blogSummaries.slice(1);

  // CF-055 design: CTA sits under copy in the featured column (not below the whole row).
  // Style 1 keeps URL-match inlining for multi-tile grids.
  const featuredGridSectionCta = (() => {
    if (!cta || !featuredBlog) return undefined;
    if (forceInlineFeaturedCta) return cta;
    if (
      inlinedBlogGridCtaBlogSysId &&
      featuredBlog?.sys?.id &&
      inlinedBlogGridCtaBlogSysId === featuredBlog.sys.id
    ) {
      return cta;
    }

    return undefined;
  })();

  const showBottomCta = Boolean(cta) && !featuredGridSectionCta && !inlinedBlogGridCtaBlogSysId;

  return (
    <Box {...styles.container} className="blog-grid__container">
      {headingText && (
        <Heading {...styles.title}>{headingText}</Heading>
      )}

      {/* Featured Blog Summary - First Row */}
      {!isComplete && isFetching ? (
        <Skeleton height="400px" width="100%" />
      ) : featuredBlog && (
        <Box {...styles.featuredWrapper}>
          <Component
            as={BlogSummary}
            component={featuredBlog}
            gridSectionCta={featuredGridSectionCta}
            isComplete={isComplete}
            isFetching={isFetching}
            slug={featuredBlog.pageSlug}
            variant="featured"
          />
        </Box>
      )}

      {/* Blog Grid - 3 Columns */}
      {gridBlogs.length > 0 && (
        <SimpleGrid {...styles.blogGrid} className="blog-grid__posts">
          {gridBlogs.map((blog, index) => (
            <Component
              key={blog.sys?.id || index}
              as={BlogSummary}
              component={blog}
              gridSectionCta={
                !forceInlineFeaturedCta &&
                inlinedBlogGridCtaBlogSysId &&
                blog?.sys?.id &&
                inlinedBlogGridCtaBlogSysId === blog.sys.id
                  ? cta
                  : undefined
              }
              isComplete={isComplete}
              isFetching={isFetching}
              slug={blog.pageSlug}
            />
          ))}
        </SimpleGrid>
      )}

      {/* CTA Link — skipped when already inlined into featured (CF-055) or a matching tile */}
      {showBottomCta ? (
        <Box
          {...styles.ctaContainer}
          className={
            ['blog-grid-listing__cta', styles.ctaContainer?.className].filter(Boolean).join(' ')
          }
        >
          <ContentfulLink component={cta} isComplete={isComplete} isFetching={isFetching} />
        </Box>
      ) : null}
    </Box>
  );
};

BlogGridStyle1.displayName = 'BlogGridStyle1';
BlogGridStyle1.propTypes = {
  component: PropTypes.object.isRequired,
  forceInlineFeaturedCta: PropTypes.bool,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  styles: PropTypes.object.isRequired,
};
